import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { generateEventSlug } from '../utils/eventUtils';
import EventDetails from './EventDetails';

const MonthDayEvent = forwardRef((props, ref) => {
    const [isDayModalOpen, setIsDayModalOpen] = useState(false);
    const [expandedEventId, setExpandedEventId] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    
    // Store the callback in a ref to avoid dependency issues
    const onModalStateChangeRef = useRef(props.onModalStateChange);
    
    // Refs for focus management
    const modalRef = useRef(null);
    const dayModalRef = useRef(null);
    const closeDayModalButtonRef = useRef(null);
    const closeModalButtonRef = useRef(null);
    const lastFocusedElement = useRef(null);
    const eventsContainerRef = useRef(null);

    const theme = props.theme || {
        eventBg: 'bg-blue-100',
        eventHeaderBg: 'bg-blue-200',
        modalBg: 'bg-white',
        modalOverlay: 'bg-black bg-opacity-50',
        text: 'text-gray-900',
        secondaryText: 'text-gray-600',
        buttonHover: 'hover:text-gray-800'
    };

    // Format the date for display
    const formatDate = (date) => {
        if (!date) return '';
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(date).toLocaleDateString('en-US', options);
    };

    // Close the modal when the parent modal is closed
    useEffect(() => {
        if (!props.isModalOpen) {
            handleCloseModal();
            handleCloseDayModal();
        }
    }, [props.isModalOpen]);


    // Update the ref when the callback changes
    useEffect(() => {
        onModalStateChangeRef.current = props.onModalStateChange;
    }, [props.onModalStateChange]);

    // Notify parent of any modal state changes
    useEffect(() => {
        const isAnyModalOpen = props.isModalOpen || isDayModalOpen;
        if (onModalStateChangeRef.current) {
            onModalStateChangeRef.current(isAnyModalOpen);
        }
    }, [props.isModalOpen, isDayModalOpen]);

    // Expose functions to parent component
    useImperativeHandle(ref, () => ({
        openDayModal: () => {
            if (props.events.length > 0) {
                setIsDayModalOpen(true);
            }
        }
    }));

    // Focus management for modals
    useEffect(() => {
        if (props.isModalOpen && selectedEvent && closeModalButtonRef.current) {
            closeModalButtonRef.current.focus();
        } else if (isDayModalOpen && closeDayModalButtonRef.current) {
            closeDayModalButtonRef.current.focus();
        } else if (!props.isModalOpen && !isDayModalOpen && lastFocusedElement.current) {
            lastFocusedElement.current.focus();
        }
    }, [props.isModalOpen, isDayModalOpen, selectedEvent]);

    // Handle keyboard events for modals
    const handleKeyDown = (e, closeFunction) => {
        if (e.key === 'Escape') {
            handleCloseModal();
            handleCloseDayModal();
        }
    };

    //const currentDate = new Date(props.currentdate);
    var todayevents = props.events;

    // Handle URL-based event selection and modal state
    useEffect(() => {
        if (props.selectedEventSlug && props.events) {
            // If there's a slug, we should close the day modal regardless
            setIsDayModalOpen(false);
            
            const event = props.events.find(e => generateEventSlug(e) === props.selectedEventSlug);
            if (event) {
                if (!selectedEvent || generateEventSlug(event) !== generateEventSlug(selectedEvent)) {
                    handleCloseDayModal();
                    setSelectedEvent(event);
                }
            } else {
                setSelectedEvent(null);
            }
        } else {
            setSelectedEvent(null);
        }
    }, [props.selectedEventSlug, props.events, selectedEvent]);

    // Add handler for opening modal
    const handleOpenModal = (event) => {
        if (selectedEvent && generateEventSlug(selectedEvent) === generateEventSlug(event)) {
            handleCloseDayModal();
            handleCloseModal();
        } else {
            setSelectedEvent(event);
            handleCloseDayModal();
            const slug = generateEventSlug(event);
            // Let the parent handle hash changes via URL
            if (window.location.hash !== `#${slug}`) {
                window.location.hash = slug;
                document.title = `${event.organization} - ${event.title} | ATLTech.events`;
            }
        }
    };

    const handleCloseDayModal = () => {
        setIsDayModalOpen(false);
        if (props.onModalClose) {
            props.onModalClose();
        }
    };

    // Add handler for closing modal
    const handleCloseModal = () => {
        setSelectedEvent(null);
        // Prevent default scroll behavior when clearing hash
        const scrollPosition = window.scrollY;
        window.location.hash = '';
        window.scrollTo(0, scrollPosition);
        document.title = 'ATLTech.events';
        if (props.onModalClose) {
            props.onModalClose();
        }
    };

    const handleExpandDetails = (event) => {
        setExpandedEventId(expandedEventId === event.id ? null : event.id);
    };

    // Add effect to manage body scroll when modals are open
    useEffect(() => {
        const isAnyModalOpen = props.isModalOpen || isDayModalOpen;
        let scrollPosition = 0;

        if (isAnyModalOpen) {

            const element = document.getElementById('scroll-here');
                    if (element) {
                        // Smooth scroll to the element
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }

            // Store current scroll position before any changes
            scrollPosition = window.scrollY || document.documentElement.scrollTop;
            
            // For iOS devices, handle scroll locking differently
            if (navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPod/i)) {
                // First set the body to fixed position while maintaining scroll position
                document.body.style.position = 'fixed';
                document.body.style.top = `-${scrollPosition}px`;
                element.scrollIntoView(true);
                document.body.style.left = '0';
                document.body.style.right = '0';
                document.body.style.width = '100%';
                //document.body.style.overflow = 'hidden';
                document.body.style.height = '100%';

            } else {
                // For other devices, just prevent scrolling
                //document.body.style.overflow = 'hidden';

            }
        } else {
            // Restore scroll position and body styles
            if (navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPod/i)) {
                const scrollY = document.body.style.top;
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.right = '';
                document.body.style.width = '';
                document.body.style.height = '';
                document.body.style.overflow = '';
                
            } else {
                document.body.style.overflow = '';
            }
        }

        return () => {
            // Cleanup function to ensure body styles are reset
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.height = '';
            document.body.style.overflow = '';
        };
    }, [props.isModalOpen, isDayModalOpen]);    

    return (
        <>
            <div className="h-full lg:mb-2 lg:pb-2 lg:mr-3">
                { todayevents.length > 0 && (
                <div className="flex items-center gap-1 justify-center mt-2 mb-2 block lg:hidden">               
                    <button 
                        onClick={() => setIsDayModalOpen(true)} 
                        className={`block lg:hidden ${theme.eventCountBg} min-w-8 rounded-full p-1 ${theme.buttonHover} transition-colors`}
                        aria-label={`Show ${todayevents.length} events for this day`}
                    >
                        {todayevents.length}
                    </button>
                </div>
                )}

                { todayevents.length === 0 && (
                    <div className="block lg:hidden flex items-center gap-1 justify-center h-8 mt-2 mb-2">
                        <p className="text-xs italic"></p>
                    </div>
                )}
               
                <div className="flex flex-col h-full hidden p-0 overflow-y-auto lg:block">
                    <div ref={eventsContainerRef} className="flex flex-col flex-grow hidden lg:block">
                    {todayevents.map(event => {
                        return (    
                            <div 
                                key={event.id} 
                                className={`hidden lg:block ${theme.eventBg} ${theme.border} border-2 mb-2 shadow-lg cursor-pointer ${
                                    event.eventType === 'Featured' ? 'bg-gradient-to-r from-blue-100 to-blue-200 border-yellow-400' : ''
                                }`}
                                onClick={() => handleOpenModal(event)}
                                aria-label={`View details for ${event.title} by ${event.organization}`}
                            >
                                <div className={`px-1 py-1 text-nowrap overflow-hidden ${theme.eventHeaderBg} font-bold text-xs max-h-18 overflow-y-auto flex justify-between items-center`}>
                                {event.eventType === 'Featured' && (
                                        <span className="bg-yellow-400 text-black text-xs px-1 py-0.5 rounded-full ml-1 mr-1" aria-label="Featured event">
                                            <i className="fas fa-star" aria-hidden="true"></i>
                                        </span>
                                    )}
                                    <span title={event.organization}>{event.organization}</span>
                                </div>
                                <div className="m-1 overflow-y-auto overflow-x-hidden">
                                    <button 
                                        className={`px-3 py-2 text-xs ${theme.buttonHover} hover:underline text-left w-full`}
                                        title={event.title}                                   
                                    >
                                        {event.title}
                                    </button>
                                    {event.sponsorLogo && (
                                        <div className="mt-2 text-center">
                                            <span className={`text-xs ${theme.secondaryText}`}>Hosted By</span>
                                            <img src={event.sponsorLogo} alt={event.sponsorName || 'Event Host'} className="max-h-8 mx-auto" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    })}    
                    </div>            
                    <div                     
                        aria-label={`Show ${todayevents.length} events for this day`}  
                        className={`text-xs italic hidden lg:block mt-auto sticky bottom-0 left-0 right-0 text-center ${theme.eventCountBg} ${theme.buttonHover} cursor-pointer`}                    
                        onClick={() => setIsDayModalOpen(true)}
                    >
                        {todayevents.length > 1 ? todayevents.length + " events" : ""}
                    </div>
                </div>

                <div className="text-xs italic hidden md:block lg:hidden">
                    {todayevents.length > 1 ? todayevents.length + " events" : todayevents.length === 1 ? "1 event" : ""}
                </div>
            </div>

            {/* Multiple events Modal - Moved outside scrollable container */}
            {isDayModalOpen && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
                    onKeyDown={(e) => handleKeyDown(e, handleCloseDayModal)}
                    onClick={handleCloseDayModal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="day-modal-title"
                >
                    <div 
                        onClick={(e) => e.stopPropagation()}
                        ref={dayModalRef}
                        className="bg-white rounded-lg w-full max-w-lg flex flex-col h-[80vh] max-h-[600px] fixed z-60 overflow-auto"
                        style={{ position: 'relative' }}
                        tabIndex="-1"
                    >
                        {/* Modal Header - Fixed position */}
                        <div className="p-4 border-b flex-shrink-0">
                            <div className="flex justify-between items-center">
                                <h2 id="day-modal-title" className="text-lg font-bold">{formatDate(props.currentdate)}</h2>
                                <button 
                                    ref={closeDayModalButtonRef}
                                    onClick={handleCloseDayModal}
                                    className={`${theme.secondaryText} ${theme.buttonHover}`}
                                    aria-label="Close"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        {/* Modal Content - Scrollable */}
                        <div className="flex-1 overflow-y-auto overscroll-contain">
                            <div className="p-4">
                                {todayevents.length === 1 ? (
                                    <EventDetails event={todayevents[0]} theme={theme} />
                                ) : (
                                    todayevents.map((event) => (
                                        <div key={event.id} className="mb-4">
                                            <div className="flex justify-between items-center p-2 ">
                                                <h3 className="text-sm text-left pr-2">
                                                    {event.eventType === 'Featured' && (
                                                        <span className="bg-yellow-400 text-black text-xs px-2 py-0.5 rounded-full mr-1" aria-label="Featured event">
                                                            <i className="fas fa-star" aria-hidden="true"></i>
                                                        </span>
                                                    )}
                                                    <span className="font-bold">{event.organization}</span> - {event.title}
                                                </h3>
                                                <button 
                                                    onClick={() => handleExpandDetails(event)}
                                                    className={`flex-shrink-0 w-8 h-8 ${theme.eventHeaderBg} ${theme.text} rounded-full ${theme.buttonHover} transition-colors`}
                                                    aria-expanded={expandedEventId === event.id}
                                                    aria-label={expandedEventId === event.id ? "Collapse event details" : "Expand event details"}
                                                >
                                                    {expandedEventId === event.id ? '−' : '+'}
                                                </button>
                                            </div>
                                            
                                            {expandedEventId === event.id && (
                                                <div className={`px-3 py-2 rounded mt-2`}>
                                                    <EventDetails event={event} theme={theme} />
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Single event Modal - Moved outside scrollable container */}
            {props.isModalOpen && selectedEvent && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
                    onKeyDown={(e) => handleKeyDown(e, handleCloseModal)}
                    onClick={handleCloseModal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="event-modal-title"
                >
                    <div 
                        onClick={(e) => e.stopPropagation()}
                        ref={modalRef}
                        className="bg-white rounded-lg w-full max-w-lg flex flex-col h-[80vh] max-h-[600px] fixed z-60"
                        style={{ position: 'relative' }}
                        tabIndex="-1"
                    >
                        {selectedEvent && (
                            <>
                                <div className={`p-4 border-b flex-shrink-0`}>
                                    <div className="flex justify-between items-center">
                                        <h2 id="event-modal-title" className="text-lg font-bold">Event Details</h2>
                                        <button 
                                            ref={closeModalButtonRef}
                                            onClick={handleCloseModal}
                                            className={`${theme.secondaryText} ${theme.buttonHover}`}
                                            aria-label="Close event details"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto overscroll-contain">
                                    <div className="p-4">
                                        <EventDetails event={selectedEvent} onClose={handleCloseModal} theme={theme} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
});

export default MonthDayEvent;
